import {
  Activity,
  ChartNoAxesCombined,
  Eye,
  HelpCircle,
  LayoutDashboard,
  Newspaper,
  Radio,
  Target,
  Zap,
} from 'lucide-react';
import type { Dispatch, SetStateAction } from 'react';
import { Box, Flex, Grid, Heading, SimpleGrid, Stack, Text, chakra } from '@chakra-ui/react';

import type { Quake, Stats, Summary } from '../api/types';
import type { DashboardControlsState } from '../hooks/useDashboardControls';
import type { PriceSamplesByAsset } from '../hooks/useLivePriceSamples';
import type { DashboardFilters } from '../lib/filters';
import { count, relativeTime } from '../lib/format';
import { AppMenu } from './AppMenu';
import { DashboardControlsPopover } from './DashboardControlsPopover';
import { EventGroupsTable } from './EventGroupsTable';
import { FeaturedMarket, type FeaturedWindow } from './FeaturedMarket';
import { FilterBar } from './FilterBar';
import { HelpModal } from './HelpModal';
import { LikelyCatalysts } from './LikelyCatalysts';
import { MarketsView } from './MarketsView';
import { MetricCard } from './MetricCard';
import { MoversView } from './MoversView';
import { RelatedMarkets } from './RelatedMarkets';
import { SignalsView } from './SignalsView';
import { StatusView } from './StatusView';
import { WatchlistView } from './WatchlistView';
import { ActionButton } from './ui/ActionButton';
import { AppNavButton, type AppNavItem } from './ui/AppNavButton';
import { FactorBar } from './ui/FactorBar';
import { GlassPanel } from './ui/GlassPanel';
import { StatusPill } from './ui/StatusPill';

export type AppTab = 'dashboard' | 'movers' | 'markets' | 'signals' | 'watchlist' | 'status';

interface DashboardProps {
  activeTab: AppTab;
  apiBaseUrl: string;
  buildLabel: string;
  controls: DashboardControlsState;
  error: string | null;
  featuredMover: Quake | undefined;
  featuredWindow: FeaturedWindow;
  filters: DashboardFilters;
  helpOpen: boolean;
  isLive: boolean;
  isLoading: boolean;
  lastMessageAt: Date | null;
  movers: Quake[];
  watchlistFallback: Quake[];
  onCloseHelp: () => void;
  onControlsChange: Dispatch<SetStateAction<DashboardControlsState>>;
  onFeaturedWindowChange: (window: Exclude<FeaturedWindow, '1D'>) => void;
  onFiltersChange: Dispatch<SetStateAction<DashboardFilters>>;
  onOpenHelp: () => void;
  onRefreshData: () => void;
  onResetControls: () => void;
  onSelectMover: (mover: Quake) => void;
  onTabChange: (tab: AppTab) => void;
  onUseTopMover: () => void;
  samplesByAsset: PriceSamplesByAsset;
  selectedMoverKey: string | undefined;
  snapshotLastAppliedAt: Date;
  stats: Stats;
  summary: Summary;
}

export function Dashboard(props: DashboardProps) {
  const apiStatus = props.error ? 'Fallback' : props.isLive ? 'Live' : 'Polling';

  return (
    <Box
      as="main"
      className="noise"
      minH="100vh"
      px={{ base: '5', lg: '8' }}
      py="4"
      color="text.primary"
    >
      <Box position="relative" zIndex="10" mx="auto" maxW="1880px">
        <TopNav
          activeTab={props.activeTab}
          apiBaseUrl={props.apiBaseUrl}
          apiStatus={apiStatus}
          buildLabel={props.buildLabel}
          isLive={props.isLive}
          onOpenHelp={props.onOpenHelp}
          onRefreshData={props.onRefreshData}
          onTabChange={props.onTabChange}
        />

        {props.activeTab === 'dashboard' ? (
          <DashboardHome {...props} />
        ) : props.activeTab === 'movers' ? (
          <MoversView
            filters={props.filters}
            onFiltersChange={props.onFiltersChange}
            autoRefresh={props.controls.autoRefresh}
            density={props.controls.tableDensity}
          />
        ) : props.activeTab === 'markets' ? (
          <MarketsView filters={props.filters} />
        ) : props.activeTab === 'signals' ? (
          <SignalsView />
        ) : props.activeTab === 'watchlist' ? (
          <WatchlistView />
        ) : (
          <StatusView />
        )}

        <CreatorFooter />
      </Box>

      <HelpModal open={props.helpOpen} onClose={props.onCloseHelp} />
    </Box>
  );
}

function DashboardHome(props: DashboardProps) {
  const topMover = props.featuredMover;
  const tableRowHeight = props.controls.tableDensity === 'compact' ? 52 : 64;
  const moverPanelHeight = 136 + props.controls.dashboardRowCount * tableRowHeight;
  const modeLabel = props.filters.mode === 'watchlist' ? 'Watchlist Event Moves' : 'Confirmed Event Moves';
  const eventCount = uniqueEventCount(props.movers);

  return (
    <>
      <Flex
        mt="8"
        flexDir={{ base: 'column', xl: 'row' }}
        gap="4"
        align={{ xl: 'flex-end' }}
        justify={{ xl: 'space-between' }}
      >
        <Stack gap="3">
          <Flex align="center" gap="3">
            <Heading as="h1" fontSize={{ base: '3xl', md: '4xl' }} fontWeight="semibold" letterSpacing="tight">
              {modeLabel}
            </Heading>
            <StatusPill tone={props.filters.mode === 'watchlist' ? 'blue' : 'good'}>
              {props.filters.mode === 'watchlist' ? 'Watchlist' : 'Live'}
            </StatusPill>
          </Flex>
          <Text fontSize={{ base: 'sm', md: 'md' }} color="text.muted">
            Heat blends move size, volume shock, capital confirmation, tail persistence, and liquidity quality.
          </Text>
        </Stack>

        <Flex align="center" gap="3">
          <GlassPanel display="flex" alignItems="center" gap="3" borderRadius="oqPanel" px="4" py="2.5">
            <Box color={props.controls.autoRefresh ? 'signal.good' : 'signal.warn'}>
              <Radio size={16} />
            </Box>
            <Text as="span" color="text.primary">
              {props.controls.autoRefresh ? 'Auto-apply' : 'Paused'}
            </Text>
            <Text as="span" display={{ base: 'none', md: 'block' }} fontSize="xs" color="text.muted">
              {relativeTime(props.snapshotLastAppliedAt)}
            </Text>
          </GlassPanel>
          <ActionButton
            onClick={props.onRefreshData}
            aria-pressed={!props.controls.autoRefresh}
            tone={props.controls.autoRefresh ? 'warn' : 'good'}
            title={props.controls.autoRefresh ? 'Pause the live feed' : 'Resume the live feed'}
          >
            {props.controls.autoRefresh ? 'Pause feed' : 'Resume feed'}
          </ActionButton>
          <DashboardControlsPopover
            controls={props.controls}
            onChange={props.onControlsChange}
            onReset={props.onResetControls}
          />
        </Flex>
      </Flex>

      <FilterBar filters={props.filters} onFiltersChange={props.onFiltersChange} />

      {props.error ? (
        <Box
          mt="3"
          borderWidth="1px"
          borderColor="signal.warn/20"
          bg="signal.warn/10"
          px="4"
          py="3"
          borderRadius="oqPanel"
        >
          <Text fontSize="sm" color="signal.warn">
            Backend unavailable or malformed response. {props.error}
          </Text>
        </Box>
      ) : null}

      <SimpleGrid mt="4" gap="3" columns={{ base: 1, md: 2, xl: 5 }}>
        <MetricCard
          icon={<Activity size={28} />}
          title={props.filters.mode === 'watchlist' ? 'Watchlist events' : 'Confirmed events'}
          value={count(eventCount)}
          detail={
            props.filters.mode === 'watchlist' && props.watchlistFallback.length > 0
              ? `${count(props.watchlistFallback.length)} candidates · heat 45-64`
              : `${count(props.movers.length)} markets currently shown`
          }
          tone="blue"
        />
        <MetricCard
          icon={<Zap size={28} />}
          title="Largest move"
          value={largestMoveLabel(props.movers)}
          detail={
            props.filters.mode === 'watchlist'
              ? 'Top |Δprice| in current window'
              : 'Top |Δprice| across both bands'
          }
          tone="green"
        />
        <MetricCard
          icon={<ChartNoAxesCombined size={28} />}
          title="Median heat"
          value={medianHeat(props.movers)}
          detail="Current visible movers"
          tone="blue"
        />
        <MetricCard
          icon={<Target size={28} />}
          title="Median reliability"
          value={medianReliability(props.movers)}
          detail="Persistence × liquidity × baseline"
          tone="orange"
        />
        <MetricCard
          icon={<Radio size={28} />}
          title="Signal model"
          value={props.filters.mode === 'watchlist' ? '45-64' : '65+'}
          detail="One heat threshold; no raw gate tuning"
          tone="red"
        />
      </SimpleGrid>

      <Box mt="3">
        <EvidenceModelCard stats={props.stats} summary={props.summary} />
      </Box>

      <Grid mt="5" gap="4" templateColumns={{ base: '1fr', xl: 'minmax(0,1.65fr) minmax(470px,0.95fr)' }}>
        <EventGroupsTable
          movers={props.movers}
          watchlistFallback={props.watchlistFallback}
          isLoading={props.isLoading}
          mode={props.filters.mode}
          rowCount={props.controls.dashboardRowCount}
          selectedMoverKey={props.selectedMoverKey}
          onSelectMover={props.onSelectMover}
          onSwitchMode={(mode) => {
            props.onFiltersChange((prev) => ({ ...prev, mode }));
          }}
          onViewAllMovers={() => {
            props.onTabChange('movers');
          }}
        />
        <LikelyCatalysts
          leader={topMover}
          height={moverPanelHeight}
          onViewAllSignals={() => {
            props.onTabChange('signals');
          }}
        />
      </Grid>

      <Grid mt="4" gap="4" templateColumns={{ base: '1fr', xl: 'minmax(0,1.65fr) minmax(470px,0.95fr)' }}>
        <FeaturedMarket
          mover={topMover}
          selectedWindow={props.featuredWindow}
          onWindowChange={props.onFeaturedWindowChange}
          onUseTopMover={props.onUseTopMover}
          watchlistFallback={props.watchlistFallback}
          samples={topMover ? props.samplesByAsset[topMover.asset_id] ?? [] : []}
        />
        {topMover ? (
          <RelatedMarkets leader={topMover} window={props.filters.window} />
        ) : (
          <RelatedMarkets window={props.filters.window} />
        )}
      </Grid>
    </>
  );
}

function TopNav({
  activeTab,
  apiBaseUrl,
  apiStatus,
  buildLabel,
  isLive,
  onOpenHelp,
  onRefreshData,
  onTabChange,
}: {
  activeTab: AppTab;
  apiBaseUrl: string;
  apiStatus: string;
  buildLabel: string;
  isLive: boolean;
  onOpenHelp: () => void;
  onRefreshData: () => void;
  onTabChange: (tab: AppTab) => void;
}) {
  const nav: readonly AppNavItem<AppTab>[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'movers', label: 'Movers', icon: ChartNoAxesCombined },
    { id: 'markets', label: 'Markets', icon: Target },
    { id: 'signals', label: 'Signals', icon: Newspaper },
    { id: 'watchlist', label: 'Watchlist', icon: Eye },
    { id: 'status', label: 'Status', icon: Radio },
  ];

  return (
    <>
      <Flex as="header" align="center" justify="space-between" borderBottomWidth="1px" borderColor="border.subtle" pb="4">
        <Flex minW="0" align="center" gap="3">
          <Logo />
          <Box minW="0">
            <Text fontSize="2xl" fontWeight="semibold" letterSpacing="tight">
              Oddsquake
            </Text>
            <Text fontSize="sm" color="text.muted" truncate>
              Real-time odds swings and internal catalysts
            </Text>
          </Box>
        </Flex>

        <Flex display={{ base: 'none', xl: 'flex' }} align="center" gap="2" as="nav" aria-label="Primary">
          {nav.map((item) => (
            <AppNavButton
              key={item.id}
              item={item}
              active={activeTab === item.id}
              onClick={() => {
                onTabChange(item.id);
              }}
            />
          ))}
        </Flex>

        <Flex display={{ base: 'none', md: 'flex' }} align="center" gap="4">
          <Box
            borderWidth="1px"
            borderColor="border.subtle"
            bg="surface.control"
            px="4"
            py="2.5"
            borderRadius="oqPanel"
          >
            <StatusPill tone={isLive ? 'good' : 'warn'}>{apiStatus}</StatusPill>
            <Text mt="1" fontSize="xs" color="text.muted">
              Streaming
            </Text>
          </Box>
          <chakra.button
            type="button"
            aria-label="Open help"
            title="Help"
            onClick={onOpenHelp}
            borderRadius="oqControl"
            p="2"
            color="text.muted"
            transition="all 0.2s"
            _hover={{ bg: 'surface.control.hovered', color: 'text.primary' }}
          >
            <HelpCircle size={20} />
          </chakra.button>
          <AppMenu
            apiBaseUrl={apiBaseUrl}
            apiStatus={apiStatus}
            buildLabel={buildLabel}
            isLive={isLive}
            onOpenAbout={onOpenHelp}
            onRefreshData={onRefreshData}
          />
        </Flex>
      </Flex>

      <Flex
        className="scrollbar-soft"
        mt="3"
        gap="2"
        overflowX="auto"
        display={{ base: 'flex', xl: 'none' }}
        as="nav"
        aria-label="Primary"
      >
        {nav.map((item) => (
          <AppNavButton
            key={item.id}
            item={item}
            active={activeTab === item.id}
            onClick={() => {
              onTabChange(item.id);
            }}
          />
        ))}
      </Flex>
    </>
  );
}

function Logo() {
  return (
    <Box
      position="relative"
      display="grid"
      placeItems="center"
      h="14"
      w="14"
      borderRadius="oqPanel"
      borderWidth="1px"
      borderColor="signal.blue/20"
      bg="signal.blue/5"
    >
      <svg viewBox="0 0 64 64" width="44" height="44" fill="none" aria-hidden="true">
        <path d="M14 42a24 24 0 1 1 34-29" stroke="#91a4bd" strokeWidth="4" strokeLinecap="round" />
        <path d="M50 24a24 24 0 0 1-36 26" stroke="#2f81f7" strokeWidth="4" strokeLinecap="round" />
        <path d="M37 8 20 35h13l-5 21 18-30H32z" fill="#ff4058" />
      </svg>
      <Box
        position="absolute"
        inset="0"
        borderRadius="oqPanel"
        boxShadow="0 0 40px var(--oq-colors-signal-blue)"
      />
    </Box>
  );
}

function CreatorFooter() {
  return (
    <Box as="footer" mt="8" borderTopWidth="1px" borderColor="border.subtle" py="6">
      <Text fontSize="xs" color="text.muted">
        <Flex flexWrap="wrap" align="center" gapX="2" gapY="1">
          <span>Built by</span>
          <chakra.a
            href="https://tulio.org"
            target="_blank"
            rel="noreferrer noopener me"
            fontWeight="medium"
            color="text.primary"
            transition="all 0.2s"
            _hover={{ color: 'signal.blue' }}
          >
            Túlio Ribeiro dos Anjos
          </chakra.a>
          <span aria-hidden="true">·</span>
          <chakra.a
            href="https://tulio.org"
            target="_blank"
            rel="noreferrer noopener me"
            aria-label="Visit tulio.org"
            transition="all 0.2s"
            _hover={{ color: 'text.primary' }}
          >
            tulio.org
          </chakra.a>
          <span aria-hidden="true">·</span>
          <chakra.a
            href="https://github.com/tulior"
            target="_blank"
            rel="noreferrer noopener me"
            aria-label="Visit Túlio Ribeiro dos Anjos on GitHub"
            transition="all 0.2s"
            _hover={{ color: 'text.primary' }}
          >
            GitHub
          </chakra.a>
        </Flex>
      </Text>
    </Box>
  );
}

function uniqueEventCount(movers: readonly Quake[]) {
  const keys = new Set<string>();
  for (const mover of movers) {
    keys.add(mover.event_slug || mover.market_id || mover.asset_id);
  }
  return keys.size;
}

function medianHeat(movers: readonly Quake[]) {
  return medianNumber(movers.map((mover) => mover.score), (value) => value.toFixed(0));
}

function largestMoveLabel(movers: readonly Quake[]): string {
  if (movers.length === 0) return '—';
  let maxAbs = 0;
  for (const mover of movers) {
    const abs = Math.abs(mover.change_pp);
    if (abs > maxAbs) maxAbs = abs;
  }
  const sign = maxAbs > 0 ? (movers.find((m) => Math.abs(m.change_pp) === maxAbs)?.change_pp ?? 0) >= 0 ? '+' : '−' : '';
  return `${sign}${maxAbs.toFixed(1)}pp`;
}

function medianReliability(movers: readonly Quake[]) {
  return medianNumber(
    movers.map((mover) => mover.reliability),
    (value) => value.toFixed(2),
  );
}

function medianNumber(values: number[], format: (value: number) => string) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (sorted.length === 0) return '—';
  const mid = sorted[Math.floor(sorted.length / 2)] ?? 0;
  return format(mid);
}

function EvidenceModelCard({ stats, summary }: { stats: Stats; summary: Summary }) {
  const factors = [
    'Move evidence',
    'Volume surprise',
    'Capital confirmation',
    'Tail persistence',
    'Liquidity quality',
    'Baseline confidence',
  ];

  // Prefer current-context stats. `stats.scoring_attempts` /
  // `stats.confirmed` / `stats.watchlist` are lifetime polling
  // counters (per-commit 3143626 renames: per-poll, per-asset; not
  // unique events). Showing them as if they were current market
  // counts is misleading. Use summary.active_assets and
  // summary.quake_events_today instead — both are true current
  // values.
  const activeAssets = summary.active_assets || stats.active_assets || 0;
  const quakesToday = summary.quake_events_today || 0;
  const matchPct = summary.markets_with_catalyst_match_pct || 0;

  return (
    <GlassPanel as="article" borderRadius="oqPanel" p="4">
      <Flex flexDir={{ base: 'column', sm: 'row' }} gap="2" align={{ sm: 'flex-end' }} justify={{ sm: 'space-between' }}>
        <Stack gap="1">
          <Text fontSize="sm" fontWeight="semibold" color="text.primary">
            Evidence model active
          </Text>
          <Text fontSize="sm" color="text.muted">
            Weak evidence lowers heat. Reliability lowers confidence. Only the final heat threshold admits a signal.
          </Text>
        </Stack>
        <Text fontSize="xs" color="text.muted">
          {count(activeAssets)} active assets · {count(quakesToday)} quakes today · {Math.round(matchPct)}% catalyst match
        </Text>
      </Flex>

      <SimpleGrid mt="4" gap="3" columns={{ base: 1, md: 3, xl: 6 }}>
        {factors.map((factor) => (
          <FactorBar key={factor} label={factor} />
        ))}
      </SimpleGrid>
    </GlassPanel>
  );
}